import { useState, useEffect, useCallback, type FormEvent } from 'react';
import type { FeeHead } from './feeStructureMockData';
import './FeeStructures.css';
import { useTranslation } from 'react-i18next';
import { fetchFeeHeadsApi, createFeeHead, FEE_TYPE_OPTIONS, FEE_FREQUENCY_OPTIONS, MONTH_OPTIONS } from '../../services/feeHeadApi';
import type { FeeType, FeeFrequency } from '../../services/feeHeadApi';
import type { ApiError } from '../../services/api';
import { fetchClasses } from '../../services/classApi';
import type { ClassApiRecord } from '../../services/classApi';
import { getSchoolId } from '../../services/auth';
import { fetchAcademicSessions } from '../../services/examApi';
import { fetchFeeStructures, createFeeStructure, addFeeStructureComponent, updateFeeStructureComponent, deleteFeeStructureComponent } from '../../services/feeStructureApi';
import type { FeeStructureRecord, FeeStructureComponentRecord } from '../../services/feeStructureApi';
import { fetchFeeDiscounts, createFeeDiscount, deleteFeeDiscount, DISCOUNT_CALC_TYPE_OPTIONS } from '../../services/feeDiscountApi';
import type { FeeDiscountRecord, DiscountCalculationType } from '../../services/feeDiscountApi';

function formatCurrency(n: number): string {
    return '₹' + n.toLocaleString('en-IN');
}

type ActiveTab = 'structures' | 'heads' | 'discounts';
type DrawerMode = null | 'create';
type FormErrors = Record<string, string>;

export default function FeeStructures() {
    const { t: _t } = useTranslation();
    const [activeTab, setActiveTab] = useState<ActiveTab>('structures');

    /* ── Reference data ── */
    const [feeHeads, setFeeHeads] = useState<FeeHead[]>([]);
    const [classes, setClasses] = useState<ClassApiRecord[]>([]);
    const [loading, setLoading] = useState(true);
    const [successMsg, setSuccessMsg] = useState('');
    const [apiError, setApiError] = useState('');
    const [search, setSearch] = useState('');

    /* ── Context from title bar + auto-detected session ── */
    const [schoolId] = useState(() => getSchoolId());
    const [sessionId, setSessionId] = useState<string | null>(null);

    /* ── Fee structures for current context ── */
    const [feeStructures, setFeeStructures] = useState<FeeStructureRecord[]>([]);
    const [structuresLoading, setStructuresLoading] = useState(false);
    const [selectedClassId, setSelectedClassId] = useState<string | null>(null);

    /* ── Auto-create structure state ── */
    const [initializingClassId, setInitializingClassId] = useState<string | null>(null);

    /* ── Add-component inline form ── */
    const [addHeadId, setAddHeadId] = useState('');
    const [addAmount, setAddAmount] = useState<number | ''>('');
    const [addingComponent, setAddingComponent] = useState(false);

    /* ── Fee Head drawer state ── */
    const [drawerMode, setDrawerMode] = useState<DrawerMode>(null);
    const [formName, setFormName] = useState('');
    const [formType, setFormType] = useState<FeeType | ''>('');
    const [formFrequency, setFormFrequency] = useState<FeeFrequency>('monthly');
    const [formBillingMonth, setFormBillingMonth] = useState<number | ''>('');
    const [formIsOptional, setFormIsOptional] = useState(false);
    const [errors, setErrors] = useState<FormErrors>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState<FeeHead | null>(null);

    /* ── Fee Discount state ── */
    const [discounts, setDiscounts] = useState<FeeDiscountRecord[]>([]);
    const [discountsLoading, setDiscountsLoading] = useState(false);
    const [discountSearch, setDiscountSearch] = useState('');
    const [discountDrawer, setDiscountDrawer] = useState(false);
    const [discountFormName, setDiscountFormName] = useState('');
    const [discountFormCalcType, setDiscountFormCalcType] = useState<DiscountCalculationType | ''>('');
    const [discountFormValue, setDiscountFormValue] = useState<number | ''>('');
    const [discountFormFeeHeadId, setDiscountFormFeeHeadId] = useState('');
    const [discountFormIsActive, setDiscountFormIsActive] = useState(true);
    const [discountErrors, setDiscountErrors] = useState<FormErrors>({});
    const [discountSubmitting, setDiscountSubmitting] = useState(false);
    const [deleteDiscountTarget, setDeleteDiscountTarget] = useState<FeeDiscountRecord | null>(null);
    const [deletingDiscount, setDeletingDiscount] = useState(false);

    /* ── Load reference data ── */
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [heads, classData, sessionData] = await Promise.all([
                    fetchFeeHeadsApi(),
                    fetchClasses({ perPage: 100 }),
                    fetchAcademicSessions(),
                ]);
                if (cancelled) return;
                setFeeHeads(heads);
                setClasses(classData.data);
                const current = sessionData.find((s) => s.is_current);
                if (current) setSessionId(current.id);
            } catch (err) {
                if (!cancelled) setApiError((err as ApiError).message || 'Failed to load data.');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    /* ── Fetch fee structures when school + session are known ── */
    useEffect(() => {
        if (!schoolId || !sessionId) { setFeeStructures([]); return; }
        let cancelled = false;
        (async () => {
            setStructuresLoading(true);
            try {
                const data = await fetchFeeStructures({ school_id: schoolId, academic_session_id: sessionId });
                if (!cancelled) setFeeStructures(data);
            } catch {
                if (!cancelled) setFeeStructures([]);
            } finally {
                if (!cancelled) setStructuresLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [schoolId, sessionId]);

    /* ── Fetch fee discounts when school is known ── */
    useEffect(() => {
        if (!schoolId) { setDiscounts([]); return; }
        let cancelled = false;
        (async () => {
            setDiscountsLoading(true);
            try {
                const data = await fetchFeeDiscounts({ school_id: schoolId });
                if (!cancelled) setDiscounts(data);
            } catch {
                if (!cancelled) setDiscounts([]);
            } finally {
                if (!cancelled) setDiscountsLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [schoolId]);

    /* ── Derived data ── */
    const structureByClass = new Map(feeStructures.map((fs) => [fs.class_id, fs]));
    const activeClassStructure = selectedClassId ? structureByClass.get(selectedClassId) ?? null : null;
    const activeClassName = classes.find((c) => c.id === selectedClassId)?.name ?? '';
    const componentTotal = activeClassStructure?.components?.reduce((sum, c) => sum + Number(c.amount), 0) ?? 0;
    /* Fee heads already in the active structure — a structure (and the invoices it
       generates) must not contain the same fee head twice */
    const structureHeadIds = new Set((activeClassStructure?.components ?? []).map((c) => c.fee_head_id));

    const filtered = feeHeads.filter((h) => {
        const q = search.toLowerCase();
        return h.name.toLowerCase().includes(q) || (h.type ?? '').toLowerCase().includes(q);
    });

    const contextReady = !!schoolId && !!sessionId;

    /* ── Auto-create fee structure when selecting an unconfigured class ── */
    const handleClassSelect = async (classId: string) => {
        setSelectedClassId(classId);
        setAddHeadId('');
        setAddAmount('');
        if (structureByClass.has(classId)) return;
        setInitializingClassId(classId);
        setApiError('');
        try {
            const created = await createFeeStructure({
                school_id: schoolId!,
                academic_session_id: sessionId!,
                class_id: classId,
                total_amount: 0,
            });
            setFeeStructures((prev) => [...prev, created]);
        } catch (err) {
            setApiError((err as ApiError).message || 'Failed to initialize fee structure.');
        } finally {
            setInitializingClassId(null);
        }
    };

    /* ── Add component to structure ── */
    const handleAddComponent = async () => {
        if (!activeClassStructure || !addHeadId || !addAmount) return;
        if (structureHeadIds.has(addHeadId)) {
            setApiError('This fee head is already in the structure — duplicate fee heads are not allowed.');
            return;
        }
        setAddingComponent(true);
        setApiError('');
        try {
            const created = await addFeeStructureComponent(activeClassStructure.id, {
                fee_head_id: addHeadId,
                amount: addAmount as number,
            });
            setFeeStructures((prev) => prev.map((fs) =>
                fs.id !== activeClassStructure.id ? fs : { ...fs, components: [...(fs.components ?? []), created] }
            ));
            setAddHeadId('');
            setAddAmount('');
            setSuccessMsg('Component added!');
            setTimeout(() => setSuccessMsg(''), 5000);
        } catch (err) {
            setApiError((err as ApiError).message || 'Failed to add component.');
        } finally {
            setAddingComponent(false);
        }
    };

    /* ── Delete component ── */
    const [deleteCompTarget, setDeleteCompTarget] = useState<FeeStructureComponentRecord | null>(null);
    const [deletingComp, setDeletingComp] = useState(false);

    const confirmDeleteComponent = async () => {
        if (!deleteCompTarget || !activeClassStructure) return;
        setDeletingComp(true);
        setApiError('');
        try {
            await deleteFeeStructureComponent(deleteCompTarget.id);
            setFeeStructures((prev) => prev.map((fs) =>
                fs.id !== activeClassStructure.id ? fs : { ...fs, components: (fs.components ?? []).filter((c) => c.id !== deleteCompTarget.id) }
            ));
            setDeleteCompTarget(null);
            setSuccessMsg('Component removed!');
            setTimeout(() => setSuccessMsg(''), 5000);
        } catch (err) {
            setApiError((err as ApiError).message || 'Failed to remove component.');
        } finally {
            setDeletingComp(false);
        }
    };

    /* ── Print fee structure ── */
    const handlePrint = () => {
        if (!activeClassStructure?.components?.length) return;
        const components = activeClassStructure.components;
        const rows = components.map((c, i) => `
            <tr>
                <td style="padding:10px 16px;border-bottom:1px solid #e5e7eb;color:#64748b;font-size:13px;width:40px">${i + 1}</td>
                <td style="padding:10px 16px;border-bottom:1px solid #e5e7eb;font-weight:500">${c.fee_head?.name ?? '—'}</td>
                <td style="padding:10px 16px;border-bottom:1px solid #e5e7eb;text-align:right;font-variant-numeric:tabular-nums">${formatCurrency(Number(c.amount))}</td>
            </tr>`).join('');

        const html = `<!DOCTYPE html>
<html><head><title>Fee Structure — ${activeClassName}</title>
<style>
  @page { margin: 20mm; }
  * { margin:0; padding:0; box-sizing:border-box; }
  body { font-family: 'Segoe UI', system-ui, -apple-system, sans-serif; color:#1e293b; padding:40px; }
  .header { text-align:center; margin-bottom:32px; padding-bottom:20px; border-bottom:2px solid #667eea; }
  .header h1 { font-size:22px; font-weight:700; color:#1e293b; margin-bottom:4px; }
  .header p { font-size:13px; color:#64748b; }
  .badge { display:inline-block; background:#f0f0ff; color:#667eea; font-size:12px; font-weight:600; padding:3px 12px; border-radius:100px; margin-top:8px; }
  table { width:100%; border-collapse:collapse; margin-bottom:0; font-size:14px; }
  thead th { padding:10px 16px; background:#f8f8fb; color:#475569; font-size:11px; font-weight:600; text-transform:uppercase; letter-spacing:0.05em; text-align:left; border-bottom:2px solid #e5e7eb; }
  thead th:last-child { text-align:right; }
  .total-row td { padding:14px 16px; font-weight:700; font-size:15px; border-top:2px solid #667eea; background:#f8f8fb; }
  .total-row td:last-child { text-align:right; color:#667eea; font-size:17px; }
  .footer { margin-top:40px; text-align:center; font-size:11px; color:#94a3b8; }
</style></head><body>
<div class="header">
  <h1>Fee Structure</h1>
  <p>${activeClassName}</p>
  <span class="badge">${components.length} component${components.length !== 1 ? 's' : ''}</span>
</div>
<table>
  <thead><tr><th style="width:40px">#</th><th>Fee Head</th><th style="text-align:right">Amount</th></tr></thead>
  <tbody>${rows}
    <tr class="total-row"><td></td><td>Total</td><td>${formatCurrency(componentTotal)}</td></tr>
  </tbody>
</table>
<div class="footer">Generated on ${new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
</body></html>`;

        const win = window.open('', '_blank');
        if (!win) return;
        win.document.write(html);
        win.document.close();
        win.addEventListener('load', () => win.print());
    };

    /* ── Inline edit component amount ── */
    const [editingCompId, setEditingCompId] = useState<string | null>(null);
    const [editAmount, setEditAmount] = useState<number | ''>('');

    const startEditAmount = (comp: FeeStructureComponentRecord) => {
        setEditingCompId(comp.id);
        setEditAmount(Number(comp.amount));
    };

    const saveEditAmount = async (comp: FeeStructureComponentRecord) => {
        setEditingCompId(null);
        if (editAmount === '' || Number(editAmount) === Number(comp.amount)) return;
        setApiError('');
        try {
            const updated = await updateFeeStructureComponent(comp.id, { amount: Number(editAmount) });
            setFeeStructures((prev) => prev.map((fs) =>
                fs.id !== comp.fee_structure_id ? fs : {
                    ...fs,
                    components: (fs.components ?? []).map((c) => c.id === comp.id ? updated : c),
                }
            ));
        } catch (err) {
            setApiError((err as ApiError).message || 'Failed to update amount.');
        }
    };

    /* ── Fee head drawer ── */
    const resetForm = useCallback(() => {
        setFormName(''); setFormType(''); setFormFrequency('monthly'); setFormBillingMonth('');
        setFormIsOptional(false);
        setErrors({});
    }, []);

    const openCreate = () => { resetForm(); setDrawerMode('create'); };
    const closeDrawer = () => { setDrawerMode(null); resetForm(); };

    const validate = (): boolean => {
        const e: FormErrors = {};
        if (!formName.trim()) e.name = 'Fee head name is required.';
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSubmit = async (ev: FormEvent) => {
        ev.preventDefault();
        if (!validate()) return;
        setIsSubmitting(true);
        setApiError('');
        try {
            const created = await createFeeHead({
                name: formName.trim(),
                ...(formType ? { type: formType } : {}),
                frequency: formFrequency,
                ...(formBillingMonth !== '' ? { billing_month: formBillingMonth } : {}),
                is_optional: formIsOptional,
            });
            setFeeHeads((prev) => [created, ...prev]);
            setSuccessMsg('Fee head created successfully!');
            closeDrawer();
            setTimeout(() => setSuccessMsg(''), 5000);
        } catch (err) {
            const apiErr = err as ApiError;
            if (apiErr.errors) {
                const fieldErrors: FormErrors = {};
                for (const [key, msgs] of Object.entries(apiErr.errors)) {
                    fieldErrors[key] = msgs[0];
                }
                setErrors(fieldErrors);
            } else {
                setApiError(apiErr.message || 'Failed to create fee head.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const confirmDelete = () => {
        if (!deleteTarget) return;
        setFeeHeads((prev) => prev.filter((h) => h.id !== deleteTarget.id));
        setDeleteTarget(null);
        setSuccessMsg('Fee head removed!');
        setTimeout(() => setSuccessMsg(''), 5000);
    };

    /* ── Discount derived data ── */
    const filteredDiscounts = discounts.filter((d) => {
        const q = discountSearch.toLowerCase();
        return d.name.toLowerCase().includes(q) || d.calculation_type.toLowerCase().includes(q);
    });

    /* ── Discount drawer helpers ── */
    const resetDiscountForm = useCallback(() => {
        setDiscountFormName(''); setDiscountFormCalcType(''); setDiscountFormValue('');
        setDiscountFormFeeHeadId(''); setDiscountFormIsActive(true); setDiscountErrors({});
    }, []);

    const openDiscountCreate = () => { resetDiscountForm(); setDiscountDrawer(true); };
    const closeDiscountDrawer = () => { setDiscountDrawer(false); resetDiscountForm(); };

    const handleDiscountSubmit = async (ev: FormEvent) => {
        ev.preventDefault();
        const e: FormErrors = {};
        if (!discountFormName.trim()) e.name = 'Discount name is required.';
        if (!discountFormCalcType) e.calculation_type = 'Calculation type is required.';
        if (discountFormValue === '' || Number(discountFormValue) < 0) e.value = 'Value must be 0 or more.';
        if (discountFormCalcType === 'percentage' && Number(discountFormValue) > 100) e.value = 'Percentage cannot exceed 100.';
        setDiscountErrors(e);
        if (Object.keys(e).length > 0) return;

        setDiscountSubmitting(true);
        setApiError('');
        try {
            const created = await createFeeDiscount({
                school_id: schoolId!,
                name: discountFormName.trim(),
                calculation_type: discountFormCalcType as DiscountCalculationType,
                value: Number(discountFormValue),
                applicable_fee_head_id: discountFormFeeHeadId || null,
                is_active: discountFormIsActive,
            });
            setDiscounts((prev) => [created, ...prev]);
            setSuccessMsg('Discount created successfully!');
            closeDiscountDrawer();
            setTimeout(() => setSuccessMsg(''), 5000);
        } catch (err) {
            const apiErr = err as ApiError;
            if (apiErr.errors) {
                const fieldErrors: FormErrors = {};
                for (const [key, msgs] of Object.entries(apiErr.errors)) {
                    fieldErrors[key] = msgs[0];
                }
                setDiscountErrors(fieldErrors);
            } else {
                setApiError(apiErr.message || 'Failed to create discount.');
            }
        } finally {
            setDiscountSubmitting(false);
        }
    };

    const confirmDeleteDiscount = async () => {
        if (!deleteDiscountTarget) return;
        setDeletingDiscount(true);
        setApiError('');
        try {
            await deleteFeeDiscount(deleteDiscountTarget.id);
            setDiscounts((prev) => prev.filter((d) => d.id !== deleteDiscountTarget.id));
            setDeleteDiscountTarget(null);
            setSuccessMsg('Discount removed!');
            setTimeout(() => setSuccessMsg(''), 5000);
        } catch (err) {
            setApiError((err as ApiError).message || 'Failed to remove discount.');
        } finally {
            setDeletingDiscount(false);
        }
    };

    if (loading) {
        return (
            <div className="fees">
                <div className="fees__loading"><div className="fees__loader"><span className="fees__loader-dot" /><span className="fees__loader-dot" /><span className="fees__loader-dot" /></div></div>
            </div>
        );
    }

    return (
        <div className="fees">
            {successMsg && (
                <div className="fees__toast">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
                    <span>{successMsg}</span>
                    <button className="fees__toast-dismiss" onClick={() => setSuccessMsg('')} aria-label="Dismiss">✕</button>
                </div>
            )}
            {apiError && (
                <div className="fees__toast" style={{ background: '#fef2f2', color: '#dc2626', borderColor: '#fecaca' }}>
                    <span>{apiError}</span>
                    <button className="fees__toast-dismiss" onClick={() => setApiError('')} aria-label="Dismiss">✕</button>
                </div>
            )}

            <div className="fees__header">
                <div className="fees__header-text">
                    <h1 className="fees__title">Fee Structures</h1>
                    <p className="fees__subtitle">Manage fee heads and class-wise fee configurations.</p>
                </div>
            </div>

            {/* ── Tab bar ── */}
            <div className="fees__tabs">
                <button className={`fees__tab ${activeTab === 'structures' ? 'fees__tab--active' : ''}`} onClick={() => setActiveTab('structures')}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /></svg>
                    Class Structures
                    <span className="fees__tab-count">{classes.length}</span>
                </button>
                <button className={`fees__tab ${activeTab === 'heads' ? 'fees__tab--active' : ''}`} onClick={() => setActiveTab('heads')}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /></svg>
                    Fee Heads
                    <span className="fees__tab-count">{feeHeads.length}</span>
                </button>
                <button className={`fees__tab ${activeTab === 'discounts' ? 'fees__tab--active' : ''}`} onClick={() => setActiveTab('discounts')}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" /><line x1="7" y1="7" x2="7.01" y2="7" /></svg>
                    Discounts
                    <span className="fees__tab-count">{discounts.length}</span>
                </button>
            </div>

            {/* ──── Tab: Class Structures ──── */}
            {activeTab === 'structures' && (
                <>
                    {!contextReady ? (
                        <div className="fees__builder">
                            <div className="fees__comp-empty" style={{ padding: 60, width: '100%', textAlign: 'center' }}>Select a school and academic session from the title bar to configure fee structures.</div>
                        </div>
                    ) : (
                        <div className="fees__builder">
                            <div className="fees__builder-left">
                                <h3 className="fees__builder-heading">Classes</h3>
                                {structuresLoading ? (
                                    <div className="fees__comp-empty">Loading…</div>
                                ) : (
                                    <div className="fees__class-list">
                                        {classes.map((c) => {
                                            const fs = structureByClass.get(c.id);
                                            const configured = !!fs;
                                            return (
                                                <button
                                                    key={c.id}
                                                    className={`fees__class-item ${selectedClassId === c.id ? 'fees__class-item--active' : ''}`}
                                                    onClick={() => handleClassSelect(c.id)}
                                                >
                                                    <div className="fees__class-info">
                                                        <span className="fees__class-name">{c.name}</span>
                                                        <span className="fees__class-meta">
                                                            {configured ? `${fs.components?.length ?? 0} components` : 'Not configured'}
                                                        </span>
                                                    </div>
                                                    {configured
                                                        ? <span className="fees__class-total">{formatCurrency(fs.components?.reduce((sum, comp) => sum + Number(comp.amount), 0) ?? 0)}</span>
                                                        : <span className="fees__class-dot" />
                                                    }
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            <div className="fees__builder-right">
                                {!selectedClassId ? (
                                    <div className="fees__comp-empty" style={{ padding: 40 }}>Select a class to configure its fee structure.</div>
                                ) : initializingClassId === selectedClassId ? (
                                    <div className="fees__comp-empty" style={{ padding: 40 }}>Initializing fee structure…</div>
                                ) : !activeClassStructure ? (
                                    <div className="fees__comp-empty" style={{ padding: 40 }}>Failed to initialize. Click the class again to retry.</div>
                                ) : (
                                    /* ── Manage components ── */
                                    <>
                                        <div className="fees__builder-right-header">
                                            <h3 className="fees__builder-heading">{activeClassName} — Components</h3>
                                            <div className="fees__builder-right-actions">
                                                <span className="fees__structure-total">Total: {formatCurrency(componentTotal)}</span>
                                                <button className="fees__print-btn" type="button" title="Print fee structure" onClick={handlePrint} disabled={!activeClassStructure?.components?.length}>
                                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 6 2 18 2 18 9" /><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" /><rect x="6" y="14" width="12" height="8" /></svg>
                                                </button>
                                            </div>
                                        </div>

                                        {/* Inline add component */}
                                        <div className="fees__add-comp-bar">
                                            <select className="fees__add-comp-select" value={addHeadId} onChange={(e) => setAddHeadId(e.target.value)}>
                                                <option value="">Fee head…</option>
                                                {feeHeads.map((h) => {
                                                    const dup = structureHeadIds.has(h.id);
                                                    return <option key={h.id} value={h.id} disabled={dup}>{h.name}{dup ? ' — already added' : ''}</option>;
                                                })}
                                            </select>
                                            <div className="fees__comp-amount-wrap">
                                                <span className="fees__comp-currency">₹</span>
                                                <input className="fees__comp-amount" type="number" min={0} placeholder="Amount" value={addAmount} onChange={(e) => setAddAmount(e.target.value === '' ? '' : Number(e.target.value))} />
                                            </div>
                                            <button className="fees__add-comp-btn" type="button" disabled={!addHeadId || !addAmount || addingComponent} onClick={handleAddComponent}>
                                                {addingComponent ? '…' : '+ Add'}
                                            </button>
                                        </div>

                                        <div className="fees__comp-list">
                                            {(!activeClassStructure.components || activeClassStructure.components.length === 0) ? (
                                                <div className="fees__comp-empty">No components yet. Add fee heads using the bar above.</div>
                                            ) : (
                                                activeClassStructure.components.map((c) => (
                                                    <div className="fees__comp-row" key={c.id}>
                                                        <span className="fees__comp-name">{c.fee_head?.name ?? '—'}</span>
                                                        {editingCompId === c.id ? (
                                                            <div className="fees__comp-amount-wrap">
                                                                <span className="fees__comp-currency">₹</span>
                                                                <input
                                                                    className="fees__comp-amount"
                                                                    type="number"
                                                                    min={0}
                                                                    value={editAmount}
                                                                    onChange={(e) => setEditAmount(e.target.value === '' ? '' : Number(e.target.value))}
                                                                    onBlur={() => saveEditAmount(c)}
                                                                    onKeyDown={(e) => { if (e.key === 'Enter') saveEditAmount(c); if (e.key === 'Escape') setEditingCompId(null); }}
                                                                    autoFocus
                                                                />
                                                            </div>
                                                        ) : (
                                                            <span className="fees__comp-amount-display" onClick={() => startEditAmount(c)} title="Click to edit">
                                                                {formatCurrency(Number(c.amount))}
                                                            </span>
                                                        )}
                                                        <button className="fees__comp-remove" type="button" title="Remove" onClick={() => setDeleteCompTarget(c)}>
                                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /></svg>
                                                        </button>
                                                    </div>
                                                ))
                                            )}
                                        </div>

                                        <div className="fees__comp-footer">
                                            <span className="fees__comp-footer-label">Components Total</span>
                                            <span className="fees__comp-footer-total">{formatCurrency(componentTotal)}</span>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* ──── Tab: Fee Heads ──── */}
            {activeTab === 'heads' && (
                <div className="fees__section">
                    <div className="fees__heads-toolbar">
                        <div className="fees__search-wrap">
                            <svg className="fees__search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                            <input className="fees__search" type="text" placeholder="Search fee heads…" value={search} onChange={(e) => setSearch(e.target.value)} />
                        </div>
                        <button className="fees__add-btn" onClick={openCreate}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                            Add Fee Head
                        </button>
                    </div>
                    <div className="fees__table-wrap">
                        <table className="fees__table">
                            <thead>
                                <tr>
                                    <th className="fees__th">Fee Head</th>
                                    <th className="fees__th">Type</th>
                                    <th className="fees__th">Optional</th>
                                    <th className="fees__th fees__th--actions">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((h) => (
                                    <tr className="fees__table-row" key={h.id}>
                                        <td className="fees__td fees__td--name">{h.name}</td>
                                        <td className="fees__td fees__td--code">{h.type ? FEE_TYPE_OPTIONS.find((o) => o.value === h.type)?.label ?? h.type : '—'}</td>
                                        <td className="fees__td">
                                            <span className={`fees__badge ${h.is_optional ? 'fees__badge--inactive' : 'fees__badge--active'}`}>
                                                {h.is_optional ? 'Optional' : 'Mandatory'}
                                            </span>
                                        </td>
                                        <td className="fees__td fees__td--actions">
                                            <button className="fees__row-btn fees__row-btn--delete" onClick={() => setDeleteTarget(h)}>
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /></svg>
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* ──── Tab: Discounts ──── */}
            {activeTab === 'discounts' && (
                <div className="fees__section">
                    <div className="fees__heads-toolbar">
                        <div className="fees__search-wrap">
                            <svg className="fees__search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                            <input className="fees__search" type="text" placeholder="Search discounts…" value={discountSearch} onChange={(e) => setDiscountSearch(e.target.value)} />
                        </div>
                        <button className="fees__add-btn" onClick={openDiscountCreate} disabled={!schoolId}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                            Add Discount
                        </button>
                    </div>
                    {discountsLoading ? (
                        <div className="fees__comp-empty" style={{ padding: 40 }}>Loading discounts…</div>
                    ) : (
                        <div className="fees__table-wrap">
                            <table className="fees__table">
                                <thead>
                                    <tr>
                                        <th className="fees__th">Name</th>
                                        <th className="fees__th">Type</th>
                                        <th className="fees__th">Value</th>
                                        <th className="fees__th">Applicable Fee Head</th>
                                        <th className="fees__th">Status</th>
                                        <th className="fees__th fees__th--actions">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredDiscounts.length === 0 ? (
                                        <tr><td className="fees__td" colSpan={6} style={{ textAlign: 'center', padding: 32, color: '#94a3b8' }}>
                                            {discounts.length === 0 ? 'No discounts configured yet.' : 'No discounts match your search.'}
                                        </td></tr>
                                    ) : filteredDiscounts.map((d) => (
                                        <tr className="fees__table-row" key={d.id}>
                                            <td className="fees__td fees__td--name">{d.name}</td>
                                            <td className="fees__td fees__td--code">{DISCOUNT_CALC_TYPE_OPTIONS.find((o) => o.value === d.calculation_type)?.label ?? d.calculation_type}</td>
                                            <td className="fees__td">{d.calculation_type === 'percentage' ? `${d.value}%` : formatCurrency(d.value)}</td>
                                            <td className="fees__td">{d.applicable_fee_head?.name ?? 'All'}</td>
                                            <td className="fees__td">
                                                <span className={`fees__badge ${d.is_active ? 'fees__badge--active' : 'fees__badge--inactive'}`}>
                                                    {d.is_active ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td className="fees__td fees__td--actions">
                                                <button className="fees__row-btn fees__row-btn--delete" onClick={() => setDeleteDiscountTarget(d)}>
                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /></svg>
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* ──── Fee Head Drawer ──── */}
            {drawerMode && (
                <>
                    <div className="fees__overlay" onClick={closeDrawer} />
                    <aside className="fees__drawer">
                        <div className="fees__drawer-header">
                            <h2 className="fees__drawer-title">Add Fee Head</h2>
                            <button className="fees__drawer-close" onClick={closeDrawer} title="Close"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg></button>
                        </div>
                        <form className="fees__drawer-body" onSubmit={handleSubmit} noValidate id="feehead-form">
                            <div className="fees__card">
                                <h3 className="fees__card-title">Fee Head Details</h3>
                                <div className="fees__field">
                                    <label className="fees__label fees__label--required" htmlFor="fhName">Name</label>
                                    <input className={`fees__input ${errors.name ? 'fees__input--error' : ''}`} type="text" id="fhName" placeholder="e.g. Tuition Fee" value={formName} maxLength={100} onChange={(e) => { setFormName(e.target.value); if (errors.name) setErrors((p) => ({ ...p, name: '' })); }} autoFocus />
                                    {errors.name && <p className="fees__error">{errors.name}</p>}
                                </div>
                                <div className="fees__field">
                                    <label className="fees__label" htmlFor="fhType">Type</label>
                                    <select className={`fees__input fees__select ${errors.type ? 'fees__input--error' : ''}`} id="fhType" value={formType} onChange={(e) => { setFormType(e.target.value as FeeType | ''); if (errors.type) setErrors((p) => ({ ...p, type: '' })); }}>
                                        <option value="">Select type…</option>
                                        {FEE_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                    </select>
                                    {errors.type && <p className="fees__error">{errors.type}</p>}
                                </div>
                                <div className="fees__field">
                                    <label className="fees__label fees__label--required" htmlFor="fhFrequency">Frequency</label>
                                    <select className={`fees__input fees__select ${errors.frequency ? 'fees__input--error' : ''}`} id="fhFrequency" value={formFrequency} onChange={(e) => { setFormFrequency(e.target.value as FeeFrequency); if (errors.frequency) setErrors((p) => ({ ...p, frequency: '' })); }}>
                                        {FEE_FREQUENCY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                    </select>
                                    {errors.frequency && <p className="fees__error">{errors.frequency}</p>}
                                </div>
                                <div className="fees__field">
                                    <label className="fees__label" htmlFor="fhBillingMonth">Preferred Month</label>
                                    <select className={`fees__input fees__select ${errors.billing_month ? 'fees__input--error' : ''}`} id="fhBillingMonth" value={formBillingMonth} onChange={(e) => { setFormBillingMonth(e.target.value === '' ? '' : Number(e.target.value)); if (errors.billing_month) setErrors((p) => ({ ...p, billing_month: '' })); }}>
                                        <option value="">No preference</option>
                                        {MONTH_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                    </select>
                                    {errors.billing_month && <p className="fees__error">{errors.billing_month}</p>}
                                    <p className="fees__field-hint">The month this fee is billed in. Applies to non-monthly frequencies.</p>
                                </div>
                            </div>
                            <div className="fees__card">
                                <h3 className="fees__card-title">Settings</h3>
                                <div className="fees__toggle-row">
                                    <div className="fees__toggle-info"><span className="fees__toggle-label">Optional</span><span className="fees__toggle-desc">Students can opt out of this fee</span></div>
                                    <label className="fees__toggle"><input type="checkbox" checked={formIsOptional} onChange={(e) => setFormIsOptional(e.target.checked)} /><span className="fees__toggle-track" /></label>
                                </div>
                            </div>
                        </form>
                        <div className="fees__drawer-footer">
                            <button className="fees__submit" type="submit" form="feehead-form" disabled={isSubmitting}>
                                {isSubmitting ? (<><svg className="fees__spinner" width="18" height="18" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" strokeWidth="3" /></svg>Creating…</>) : 'Create Fee Head'}
                            </button>
                            <button className="fees__cancel" type="button" onClick={closeDrawer} disabled={isSubmitting}>Cancel</button>
                        </div>
                    </aside>
                </>
            )}

            {/* Delete fee head confirm */}
            {deleteTarget && (
                <div className="fees__confirm-overlay" onClick={() => setDeleteTarget(null)}>
                    <div className="fees__confirm-modal" onClick={(e) => e.stopPropagation()}>
                        <h3 className="fees__confirm-title">Remove Fee Head</h3>
                        <p className="fees__confirm-text">Are you sure you want to remove <strong>{deleteTarget.name}</strong>? This action can be undone by an administrator.</p>
                        <div className="fees__confirm-actions">
                            <button className="fees__confirm-cancel" onClick={() => setDeleteTarget(null)}>Cancel</button>
                            <button className="fees__confirm-delete" onClick={confirmDelete}>Remove</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ──── Discount Drawer ──── */}
            {discountDrawer && (
                <>
                    <div className="fees__overlay" onClick={closeDiscountDrawer} />
                    <aside className="fees__drawer">
                        <div className="fees__drawer-header">
                            <h2 className="fees__drawer-title">Add Discount</h2>
                            <button className="fees__drawer-close" onClick={closeDiscountDrawer} title="Close"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg></button>
                        </div>
                        <form className="fees__drawer-body" onSubmit={handleDiscountSubmit} noValidate id="discount-form">
                            <div className="fees__card">
                                <h3 className="fees__card-title">Discount Details</h3>
                                <div className="fees__field">
                                    <label className="fees__label fees__label--required" htmlFor="discName">Name</label>
                                    <input className={`fees__input ${discountErrors.name ? 'fees__input--error' : ''}`} type="text" id="discName" placeholder="e.g. Sibling Discount" value={discountFormName} maxLength={100} onChange={(e) => { setDiscountFormName(e.target.value); if (discountErrors.name) setDiscountErrors((p) => ({ ...p, name: '' })); }} autoFocus />
                                    {discountErrors.name && <p className="fees__error">{discountErrors.name}</p>}
                                </div>
                                <div className="fees__field">
                                    <label className="fees__label fees__label--required" htmlFor="discCalcType">Calculation Type</label>
                                    <select className={`fees__input fees__select ${discountErrors.calculation_type ? 'fees__input--error' : ''}`} id="discCalcType" value={discountFormCalcType} onChange={(e) => { setDiscountFormCalcType(e.target.value as DiscountCalculationType | ''); if (discountErrors.calculation_type) setDiscountErrors((p) => ({ ...p, calculation_type: '' })); }}>
                                        <option value="">Select type…</option>
                                        {DISCOUNT_CALC_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                    </select>
                                    {discountErrors.calculation_type && <p className="fees__error">{discountErrors.calculation_type}</p>}
                                </div>
                                <div className="fees__field">
                                    <label className="fees__label fees__label--required" htmlFor="discValue">Value</label>
                                    <input className={`fees__input ${discountErrors.value ? 'fees__input--error' : ''}`} type="number" id="discValue" min={0} max={discountFormCalcType === 'percentage' ? 100 : undefined} placeholder={discountFormCalcType === 'percentage' ? 'e.g. 10' : 'e.g. 500'} value={discountFormValue} onChange={(e) => { setDiscountFormValue(e.target.value === '' ? '' : Number(e.target.value)); if (discountErrors.value) setDiscountErrors((p) => ({ ...p, value: '' })); }} />
                                    {discountErrors.value && <p className="fees__error">{discountErrors.value}</p>}
                                </div>
                                <div className="fees__field">
                                    <label className="fees__label" htmlFor="discFeeHead">Applicable Fee Head</label>
                                    <select className="fees__input fees__select" id="discFeeHead" value={discountFormFeeHeadId} onChange={(e) => setDiscountFormFeeHeadId(e.target.value)}>
                                        <option value="">All fee heads</option>
                                        {feeHeads.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div className="fees__card">
                                <h3 className="fees__card-title">Settings</h3>
                                <div className="fees__toggle-row">
                                    <div className="fees__toggle-info"><span className="fees__toggle-label">Active</span><span className="fees__toggle-desc">Enable this discount for fee calculations</span></div>
                                    <label className="fees__toggle"><input type="checkbox" checked={discountFormIsActive} onChange={(e) => setDiscountFormIsActive(e.target.checked)} /><span className="fees__toggle-track" /></label>
                                </div>
                            </div>
                        </form>
                        <div className="fees__drawer-footer">
                            <button className="fees__submit" type="submit" form="discount-form" disabled={discountSubmitting}>
                                {discountSubmitting ? (<><svg className="fees__spinner" width="18" height="18" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" strokeWidth="3" /></svg>Creating…</>) : 'Create Discount'}
                            </button>
                            <button className="fees__cancel" type="button" onClick={closeDiscountDrawer} disabled={discountSubmitting}>Cancel</button>
                        </div>
                    </aside>
                </>
            )}

            {/* Delete discount confirm */}
            {deleteDiscountTarget && (
                <div className="fees__confirm-overlay" onClick={() => { if (!deletingDiscount) setDeleteDiscountTarget(null); }}>
                    <div className="fees__confirm-modal" onClick={(e) => e.stopPropagation()}>
                        <h3 className="fees__confirm-title">Remove Discount</h3>
                        <p className="fees__confirm-text">Are you sure you want to remove <strong>{deleteDiscountTarget.name}</strong>? This action cannot be undone.</p>
                        <div className="fees__confirm-actions">
                            <button className="fees__confirm-cancel" onClick={() => setDeleteDiscountTarget(null)} disabled={deletingDiscount}>Cancel</button>
                            <button className="fees__confirm-delete" onClick={confirmDeleteDiscount} disabled={deletingDiscount}>{deletingDiscount ? 'Removing…' : 'Remove'}</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete component confirm */}
            {deleteCompTarget && (
                <div className="fees__confirm-overlay" onClick={() => { if (!deletingComp) setDeleteCompTarget(null); }}>
                    <div className="fees__confirm-modal" onClick={(e) => e.stopPropagation()}>
                        <h3 className="fees__confirm-title">Remove Component</h3>
                        <p className="fees__confirm-text">Are you sure you want to remove <strong>{deleteCompTarget.fee_head?.name ?? 'this component'}</strong> ({formatCurrency(Number(deleteCompTarget.amount))})?</p>
                        <div className="fees__confirm-actions">
                            <button className="fees__confirm-cancel" onClick={() => setDeleteCompTarget(null)} disabled={deletingComp}>Cancel</button>
                            <button className="fees__confirm-delete" onClick={confirmDeleteComponent} disabled={deletingComp}>{deletingComp ? 'Removing…' : 'Remove'}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
